
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Mountain, Award, Users, Heart, Zap, TrendingUp } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

export const AdvancedStravaAnalytics: React.FC = () => {
  const { user } = useAuth();

  // Fetch Strava activities with detailed metrics
  const { data: stravaActivities, isLoading } = useQuery({
    queryKey: ['strava-activities-detailed', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('running_performance')
        .select('*')
        .eq('user_id', user.id)
        .eq('data_source', 'strava')
        .not('strava_activity_id', 'is', null)
        .order('recorded_date', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  // Calculate Strava-specific metrics
  const stravaMetrics = React.useMemo(() => {
    if (!stravaActivities?.length) return null;

    const totalActivities = stravaActivities.length;
    const totalKudos = stravaActivities.reduce((sum, act) => sum + (act.kudos_count || 0), 0);
    const totalSufferScore = stravaActivities.reduce((sum, act) => sum + (act.suffer_score || 0), 0);
    const avgSufferScore = totalSufferScore / totalActivities;
    
    const segmentEfforts = stravaActivities.filter(act => 
      act.strava_segment_efforts && Array.isArray(act.strava_segment_efforts) && act.strava_segment_efforts.length > 0
    ).length;

    const manualActivities = stravaActivities.filter(act => act.manual).length;
    const deviceActivities = totalActivities - manualActivities;

    return {
      totalActivities,
      totalKudos,
      avgSufferScore,
      segmentEfforts,
      manualActivities,
      deviceActivities,
      automationRate: (deviceActivities / totalActivities) * 100
    };
  }, [stravaActivities]);

  // Prepare suffer score distribution data
  const sufferScoreData = React.useMemo(() => {
    if (!stravaActivities?.length) return [];

    const buckets = {
      'Easy (0-25)': 0,
      'Moderate (26-50)': 0,
      'Hard (51-75)': 0,
      'Very Hard (76-100)': 0,
      'Extreme (100+)': 0
    };

    stravaActivities.forEach(activity => {
      const score = activity.suffer_score || 0;
      if (score <= 25) buckets['Easy (0-25)']++;
      else if (score <= 50) buckets['Moderate (26-50)']++;
      else if (score <= 75) buckets['Hard (51-75)']++;
      else if (score <= 100) buckets['Very Hard (76-100)']++;
      else buckets['Extreme (100+)']++;
    });

    return Object.entries(buckets).map(([name, value]) => ({ name, value }));
  }, [stravaActivities]);

  // Prepare monthly kudos data
  const monthlyKudosData = React.useMemo(() => {
    if (!stravaActivities?.length) return [];

    const monthlyData: Record<string, number> = {};
    
    stravaActivities.forEach(activity => {
      const month = new Date(activity.recorded_date).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short' 
      });
      monthlyData[month] = (monthlyData[month] || 0) + (activity.kudos_count || 0);
    });

    return Object.entries(monthlyData)
      .map(([month, kudos]) => ({ month, kudos }))
      .sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime())
      .slice(-6); // Last 6 months
  }, [stravaActivities]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-muted rounded w-3/4"></div>
            </CardHeader>
            <CardContent>
              <div className="h-20 bg-muted rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!stravaMetrics || stravaActivities?.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <Mountain className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Strava Data Found</h3>
            <p className="text-muted-foreground">
              Connect your Strava account or sync your activities to see advanced analytics.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Strava Overview Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Mountain className="h-4 w-4 text-orange-600" />
              Strava Activities
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stravaMetrics.totalActivities}</div>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                {stravaMetrics.deviceActivities} auto
              </Badge>
              <Badge variant="secondary" className="bg-gray-100 text-gray-800">
                {stravaMetrics.manualActivities} manual
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Heart className="h-4 w-4 text-red-600" />
              Total Kudos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stravaMetrics.totalKudos}</div>
            <div className="text-sm text-muted-foreground mt-1">
              {(stravaMetrics.totalKudos / stravaMetrics.totalActivities).toFixed(1)} avg per activity
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Zap className="h-4 w-4 text-yellow-600" />
              Avg Suffer Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stravaMetrics.avgSufferScore.toFixed(0)}</div>
            <Badge 
              variant="secondary" 
              className={`mt-1 ${
                stravaMetrics.avgSufferScore > 60 ? 'bg-red-100 text-red-800' :
                stravaMetrics.avgSufferScore > 30 ? 'bg-orange-100 text-orange-800' :
                'bg-green-100 text-green-800'
              }`}
            >
              {stravaMetrics.avgSufferScore > 60 ? 'High Intensity' :
               stravaMetrics.avgSufferScore > 30 ? 'Moderate' : 'Easy'}
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Award className="h-4 w-4 text-purple-600" />
              Segment Efforts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stravaMetrics.segmentEfforts}</div>
            <div className="text-sm text-muted-foreground mt-1">
              {((stravaMetrics.segmentEfforts / stravaMetrics.totalActivities) * 100).toFixed(0)}% of activities
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Automation Rate */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Data Quality & Automation
          </CardTitle>
          <CardDescription>
            How much of your training data is automatically captured vs manually entered
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span>Automatic Device Sync</span>
                <span>{stravaMetrics.automationRate.toFixed(1)}%</span>
              </div>
              <Progress value={stravaMetrics.automationRate} className="h-2" />
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="text-center p-3 bg-green-50 border border-green-200 rounded">
                <div className="text-2xl font-bold text-green-800">{stravaMetrics.deviceActivities}</div>
                <div className="text-sm text-green-600">Device Recorded</div>
              </div>
              <div className="text-center p-3 bg-orange-50 border border-orange-200 rounded">
                <div className="text-2xl font-bold text-orange-800">{stravaMetrics.manualActivities}</div>
                <div className="text-sm text-orange-600">Manual Entry</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Suffer Score Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Suffer Score Distribution
            </CardTitle>
            <CardDescription>
              Breakdown of training intensity based on Strava's Suffer Score
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sufferScoreData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value, percent }) => 
                      `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {sufferScoreData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Monthly Kudos Trend
            </CardTitle>
            <CardDescription>
              Social engagement and community interaction over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyKudosData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="kudos" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Advanced Strava Insights */}
      <Card>
        <CardHeader>
          <CardTitle>Advanced Strava Insights</CardTitle>
          <CardDescription>
            Deep analysis of your Strava-specific metrics and patterns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-medium">Training Intensity Analysis</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm">Easy Days (Suffer Score 0-25)</span>
                  <span className="text-sm font-medium">
                    {sufferScoreData.find(d => d.name.includes('Easy'))?.value || 0} activities
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Hard Days (Suffer Score 51+)</span>
                  <span className="text-sm font-medium">
                    {(sufferScoreData.find(d => d.name.includes('Hard'))?.value || 0) + 
                     (sufferScoreData.find(d => d.name.includes('Very Hard'))?.value || 0) + 
                     (sufferScoreData.find(d => d.name.includes('Extreme'))?.value || 0)} activities
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  Ideal ratio: 80% easy, 20% moderate to hard
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-medium">Data Quality Score</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm">Automation Rate</span>
                  <Badge variant="secondary" className={`${
                    stravaMetrics.automationRate > 80 ? 'bg-green-100 text-green-800' :
                    stravaMetrics.automationRate > 60 ? 'bg-orange-100 text-orange-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {stravaMetrics.automationRate.toFixed(0)}%
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Heart Rate Data</span>
                  <Badge variant="secondary">
                    {stravaActivities?.filter(a => a.heart_rate_avg).length || 0} activities
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  Higher automation and HR data = better insights
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
